import { Check } from 'lucide-react';
import { LAYER } from './layers';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../auth/AuthContext';
import { CountBadge } from './CountBadge';
import { Tooltip } from './Tooltip';

export interface MenuItem {
  key: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  /** Permission key. A caller without it does not see the item at all. */
  permission?: string;
  /** Renders red. Reserved for Delete and equivalents. */
  destructive?: boolean;
  /**
   * Non-null disables the item AND explains why. An item is either actionable or it says
   * what would make it actionable — never silently dead.
   */
  disabledReason?: string | null;
  /** Draws a divider above this item. */
  divider?: boolean;
  /**
   * Marks the item as a **toggle** rather than a one-shot action: it renders a tick when on,
   * and is announced as `menuitemcheckbox` rather than `menuitem`.
   *
   * For a control the reader turns on and leaves on — the index cog's view scopes. An ordinary
   * action leaves it undefined and gets no tick gutter of its own.
   */
  checked?: boolean;
  /**
   * A count, pinned to the row’s trailing edge — how many records the entry leads to.
   *
   * `undefined` renders nothing and `0` renders a visible zero, which is `<CountBadge>`’s own
   * distinction and the reason to use it rather than a number in the label: a request still in
   * flight must not look like an empty result.
   */
  badge?: number;

  /**
   * Groups this item under an uppercase heading — "View", "Records".
   *
   * The heading is drawn once, above the first item carrying it, with a rule above every
   * section but the first. It exists so a settings cog built from `<Menu>` reads like the
   * index page's hand-rolled one, which has had these headings all along: the panel cog was a
   * flat run of "Table / List / Show deleted" where the index cog separated the view switch
   * from what it was switching over.
   *
   * Leave it off for a row-action menu. A list of verbs does not need a heading over it, and
   * one section heading in a menu of four actions is chrome the reader has to read past.
   */
  section?: string;
}

/**
 * The shared dropdown menu. Owns positioning, dismissal, and permission filtering so no
 * caller re-implements them.
 *
 * Permission-gated items are **hidden**, not disabled (reference §4): a control the user
 * can never enable is noise. Disabling is for state, and then `disabledReason` is required.
 */
export function Menu({
  trigger,
  items,
  align = 'right',
  contextMenuFor,
  id,
}: {
  /**
   * Rendered as the button. Receives nothing — the wrapper owns open state.
   *
   * **Optional, and omitting it makes a BACKGROUND context menu** — a menu with no visible
   * affordance at all, opened only by right-clicking the region `contextMenuFor` names. The
   * repository's folder canvas is the one surface that has one, and it is the file-explorer
   * gesture: right-click the empty space to create or upload.
   *
   * A menu nobody can reach by keyboard is only acceptable because such a menu is a
   * SHORTCUT — every entry in it must also exist somewhere a reader can tab to, exactly as a
   * row's right-click duplicates its kebab. Build it any other way and the actions are
   * unreachable without a pointer, which is a WCAG 2.1.1 failure rather than a tidier toolbar.
   */
  trigger?: (props: { open: boolean; toggle: () => void }) => ReactNode;
  items: MenuItem[];
  align?: 'left' | 'right';
  /**
   * A CSS selector for the ancestor that should also open this menu on right-click —
   * `'tr, li'` for a row, a data attribute for a panel's canvas. Opt-in, so a standalone menu
   * keeps the browser's own. **Required when `trigger` is omitted**, or nothing can open it.
   */
  contextMenuFor?: string;
  id: string;
}) {
  const { hasPermission } = useAuth();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  /** Set while the menu was opened by right-click, so it positions at the pointer. */
  const [point, setPoint] = useState<{ x: number; y: number } | null>(null);

  const anchorRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const visible = items.filter((item) => !item.permission || hasPermission(item.permission));

  /*
    Whether to reserve the leading icon gutter. Computed over the VISIBLE items, so a menu
    whose only iconed entry was permission-gated away does not keep an indent for it.
  */
  const reserveIconGutter = visible.some((item) => item.icon || item.checked !== undefined);

  /*
    Right-click anywhere on the row opens this same menu.

    The listener is attached to the nearest matching ancestor rather than to a wrapper of
    our own, because the trigger already lives inside the row on every surface that has one
    — so the table row and the card row both get it without either of them knowing.
  */
  useEffect(() => {
    if (!contextMenuFor) {
      return;
    }

    const row = anchorRef.current?.closest(contextMenuFor);

    if (!(row instanceof HTMLElement)) {
      return;
    }

    function onContextMenu(event: MouseEvent) {
      /*
        The row nearest the pointer owns the gesture, and says so before deciding what to do
        with it.

        Rows nest on some surfaces — a tree's `<li>` contains its descendants' `<li>`s — so
        without this the event bubbles on to every ancestor row, whose menu has a listener on
        its own element, and one right-click opens the whole chain of them. Stopping first
        rather than after the guard below means an ancestor cannot claim a click that landed
        on a row inside it, even when that row has nothing to offer.
      */
      event.stopPropagation();

      // A menu with nothing in it must not swallow the browser's own.
      if (visible.length === 0) {
        return;
      }

      event.preventDefault();
      setPoint({ x: event.clientX, y: event.clientY });
      setOpen(true);
    }

    row.addEventListener('contextmenu', onContextMenu);

    return () => row.removeEventListener('contextmenu', onContextMenu);
  }, [contextMenuFor, visible.length]);

  // Fixed positioning so the panel escapes the table's overflow container, flipping up
  // when there is not enough room below.
  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    const estimated = 8 + visible.length * 36;

    if (point) {
      // Opened by right-click: the pointer is the anchor. Clamped so a click near the
      // right or bottom edge does not put the panel off-screen.
      setPosition({
        top: point.y + estimated > window.innerHeight ? Math.max(8, point.y - estimated) : point.y,
        left: Math.max(8, Math.min(point.x, window.innerWidth - 216)),
      });

      return;
    }

    if (!anchorRef.current) {
      return;
    }

    const bounds = anchorRef.current.getBoundingClientRect();
    const flipUp = bounds.bottom + estimated > window.innerHeight && bounds.top > estimated;

    setPosition({
      top: flipUp ? bounds.top - estimated - 4 : bounds.bottom + 4,
      left: align === 'right' ? Math.max(8, bounds.right - 208) : bounds.left,
    });
  }, [open, visible.length, align, point]);

  // Forget the pointer once closed, or the next kebab click would reopen where the last
  // right-click happened.
  useEffect(() => {
    if (!open) setPoint(null);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;

      if (!panelRef.current?.contains(target) && !anchorRef.current?.contains(target)) {
        setOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    // The row scrolls away underneath, so closing beats repositioning here.
    function onScroll(event: Event) {
      if (!panelRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, true);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  /*
    `contents` when there is no trigger, so a background menu's anchor occupies no space in
    whatever layout it was dropped into. It stays in the DOM either way — `closest()` walks up
    from it to find the region, so removing it would remove the only way the menu opens.
  */
  return (
    <span ref={anchorRef} className={trigger ? 'inline-flex' : 'contents'}>
      {trigger?.({ open, toggle: () => setOpen((v) => !v) })}

      {open && position
        ? createPortal(
            <div
              ref={panelRef}
              id={id}
              role="menu"
              style={{ position: 'fixed', top: position.top, left: position.left, width: 208 }}
              className={`${LAYER.PANEL} rounded-lg border border-slate-200 bg-white py-1 shadow-raised`}
            >
              {visible.map((item, index) => {
                const disabled = Boolean(item.disabledReason);

                /*
                  A heading is drawn once, above the first item of its section. Compared against
                  the PREVIOUS VISIBLE item rather than against a running list, so a permission
                  gate that hides a whole section takes the heading with it instead of leaving
                  one over the section below.
                */
                const previous = index > 0 ? visible[index - 1] : undefined;
                const startsSection = Boolean(item.section) && item.section !== previous?.section;

                // Every section but the first opens with a rule, matching the index cog.
                const rule = startsSection ? index > 0 : item.divider;

                return (
                  <div key={item.key}>
                    {rule ? (
                      <div className="my-1.5 border-t border-slate-200" role="separator" />
                    ) : null}

                    {startsSection ? (
                      <p className="px-3 pb-1 pt-1.5 text-[10px] uppercase tracking-wider text-gray-600">
                        {item.section}
                      </p>
                    ) : null}

                  {/* The reason a disabled entry is disabled, in the product’s own tooltip. */}
                  <Tooltip disabledReason={item.disabledReason ?? null} block>
                    <button
                      type="button"
                      /*
                        A TOGGLE announces itself as one. `checked` was declared on `MenuItem`
                        and never read, so the panel cog's view switch drew its tick through
                        `icon` and was announced as a plain action — a control a screen reader
                        could not tell was on.
                      */
                      role={item.checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
                      aria-checked={item.checked}
                      id={`${id}-${item.key}`}
                      disabled={disabled}
                      onClick={() => {
                        setOpen(false);
                        item.onClick();
                      }}
                      className={[
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition',
                        disabled
                          ? 'cursor-not-allowed text-slate-300'
                          : item.destructive
                            ? 'text-red-700 hover:bg-red-50'
                            : 'text-slate-600 hover:bg-slate-50',
                      ].join(' ')}
                    >
                      {/*
                        A FIXED gutter, so the labels line up whether or not an item carries an
                        icon — which is what the index cog does and what this menu did not. A
                        ticked "List" beside an unticked "Table" put the two labels at different
                        x positions, and the eye reads that as two unrelated controls.

                        Reserved only when SOMETHING in the menu has an icon: a menu of plain
                        verbs has nothing to line up against, and an empty gutter there would be
                        an indent with no cause.
                      */}
                      {reserveIconGutter ? (
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                          {/*
                            The tick is the state indicator, exactly as it is in the index
                            cog's hand-rolled sections — and it shares the gutter with the
                            decorative icons, so a menu mixing the two keeps one indent.
                          */}
                          {item.checked ? (
                            <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />
                          ) : (
                            item.icon
                          )}
                        </span>
                      ) : null}
                      {/* Its own item, so a long label truncates instead of shoving the badge out. */}
                      <span className="min-w-0 flex-1 truncate">{item.label}</span>
                      {/* Slate: a count of records is a quantity, not a state worth colouring. */}
                      <CountBadge count={item.badge} tone="slate" className="ml-auto shrink-0" />
                    </button>
                  </Tooltip>
                  </div>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
